import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { registerForPushNotifications } from "@/lib/pushNotifications";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, phone?: string, options?: { referralCode?: string; instagramHandle?: string }) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Track sign-in activity for admin dashboard
      if (event === "SIGNED_IN" && session?.user) {
        const userId = session.user.id;
        supabase
          .from("profiles")
          .update({ last_sign_in_at: new Date().toISOString() })
          .eq("user_id", userId)
          .then(() => {});

        // Register native push notifications (no-op on web)
        registerForPushNotifications(userId).catch((err) =>
          console.warn("Push registration failed:", err)
        );
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string, phone?: string, options?: { referralCode?: string; instagramHandle?: string }) => {
    // Email is REQUIRED — even when sign-up starts with a phone number.
    // Reject empty values and the legacy phone-based placeholder domain.
    const trimmedEmail = (email || "").trim().toLowerCase();
    const isPlaceholder = trimmedEmail.endsWith("@phone.inparadisebeauty.local") || trimmedEmail.includes("@phone.inparadisebeauty");
    const isValidShape = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    if (!trimmedEmail || isPlaceholder || !isValidShape) {
      return { error: new Error("A valid email address is required to create an account.") };
    }

    const { data, error } = await supabase.auth.signUp({
      email: trimmedEmail,
      password,
      options: {
        data: { full_name: fullName, phone },
        emailRedirectTo: window.location.origin,
      },
    });

    // If signup succeeded, update the profile with additional info
    if (!error && data.user) {
      const profileUpdate: Record<string, string | null> = {};
      if (phone) profileUpdate.phone = phone;
      if (options?.instagramHandle) profileUpdate.instagram_link = options.instagramHandle;
      if (options?.referralCode) profileUpdate.signup_referral_code = options.referralCode.toUpperCase();
      
      if (Object.keys(profileUpdate).length > 0) {
        await supabase
          .from("profiles")
          .update(profileUpdate)
          .eq("user_id", data.user.id);
      }
    }

    // Call auto-confirm to handle placeholder emails or when email confirmation is disabled
    if (!error && data.user) {
      try {
        const { data: confirmData } = await supabase.functions.invoke("auto-confirm-user", {
          body: { user_id: data.user.id },
        });
        // If email confirmation is required, sign the user out so they can't access the app
        if (confirmData?.requires_email_confirmation) {
          await supabase.auth.signOut();
        }
      } catch (e) {
        // Non-critical — user may just need to confirm email
        console.warn("Auto-confirm check failed:", e);
      }
    }

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
